import type { AuthUser } from '../../../redux/types';

type SegmentTraits = Record<string, unknown>;

interface SegmentAnalytics {
  identify: ((traits: SegmentTraits) => void) &
  ((userId: string, traits?: SegmentTraits) => void);
  addSourceMiddleware?: (middleware: (args: {
    payload: {
      obj?: {
        type?: string;
        context?: SegmentTraits & { event?: { type?: string } };
      };
    };
    next: (payload: unknown) => void;
  }) => void) => void;
  reset: () => void;
}

const INVALID_USER_IDS = new Set(['anonymous', 'null', 'undefined']);

export function toSegmentUserId(id: unknown): string | null {
  if (id === null || id === undefined) return null;
  const normalized = String(id).trim();
  if (!normalized || INVALID_USER_IDS.has(normalized.toLowerCase())) {
    return null;
  }
  return normalized;
}

export function isValidSegmentUserId(id: unknown): boolean {
  return toSegmentUserId(id) !== null;
}

function getAnalytics(): SegmentAnalytics | null {
  if (typeof window === 'undefined' || !window.analytics) return null;
  return window.analytics;
}

let lastIdentifiedUserId: string | null = null;
let lastIdentifiedTraitsHash: string | null = null;
let lastIdentifiedUserEmail: string | null = null;
let analyticsWithRegisteredEventContextMiddleware: SegmentAnalytics | null =
  null;

function hashTraits(traits: SegmentTraits): string {
  try {
    return JSON.stringify(traits);
  } catch {
    return '';
  }
}

type AuthenticatedUser = Pick<
  AuthUser,
  'id' | 'email' | 'name' | 'client_id' | 'created_at'
>;

type SegmentIdentifyTraits = SegmentTraits & {
  subscription_status?: string;
  plan_name?: string;
};

export function identifyAuthenticatedUser(
  user: AuthenticatedUser | null | undefined,
  extraTraits: SegmentIdentifyTraits = {},
): void {
  const userId = toSegmentUserId(user?.id);
  if (!userId || !user?.email) return;

  const analytics = getAnalytics();
  if (!analytics) return;

  const traits: SegmentTraits = {
    email: user.email,
    name: user.name,
    client_id: user.client_id,
    created_at: user.created_at,
    ...extraTraits,
  };

  const traitsHash = hashTraits(traits);
  if (userId === lastIdentifiedUserId && traitsHash === lastIdentifiedTraitsHash) {
    return;
  }

  analytics.identify(userId, traits);
  lastIdentifiedUserId = userId;
  lastIdentifiedUserEmail = user.email;
  lastIdentifiedTraitsHash = traitsHash;
  registerSegmentEventContextMiddleware();
}

export function identifyAfterSignup(
  userId: unknown,
  traits: SegmentTraits = {},
): void {
  const segmentUserId = toSegmentUserId(userId);
  if (!segmentUserId) return;

  const analytics = getAnalytics();
  if (!analytics) return;

  analytics.identify(segmentUserId, traits);
  lastIdentifiedUserId = segmentUserId;
  lastIdentifiedUserEmail = null;
  lastIdentifiedTraitsHash = hashTraits(traits);
  registerSegmentEventContextMiddleware();
}

export function identifyAnonymousTraits(traits: SegmentTraits): void {
  if (!traits || Object.keys(traits).length === 0) return;
  getAnalytics()?.identify(traits);
}

export function getLastIdentifiedSegmentUserId(): string | null {
  return lastIdentifiedUserId;
}

export function getLastIdentifiedSegmentUserEmail(): string | null {
  return lastIdentifiedUserEmail;
}

export function buildSegmentEventContext(
  context: SegmentTraits = {},
): SegmentTraits {
  const email = getLastIdentifiedSegmentUserEmail();
  if (!email) {
    return {
      ...context,
      source: 'frontend',
    };
  }

  return {
    ...context,
    source: 'frontend',
    traits: {
      ...((context.traits as SegmentTraits | undefined) ?? {}),
      email,
    },
  };
}

export function registerSegmentEventContextMiddleware(): void {
  const analytics = getAnalytics();
  if (
    !analytics?.addSourceMiddleware ||
    analyticsWithRegisteredEventContextMiddleware === analytics
  ) {
    return;
  }

  analytics.addSourceMiddleware(({ payload, next }) => {
    const eventType = payload.obj?.type ?? payload.obj?.context?.event?.type;

    if (payload.obj && (eventType === 'track' || eventType === 'page')) {
      payload.obj.context = buildSegmentEventContext(payload.obj.context ?? {});
    }

    next(payload);
  });

  analyticsWithRegisteredEventContextMiddleware = analytics;
}

export type SegmentUserEventData = {
  id?: unknown;
  email?: string;
  name?: string;
  client_id?: unknown;
  jusbill_customer_id?: unknown;
};

/**
 * Propriedades de usuário para payloads de track (checkout, upgrade, SegmentProvider).
 * Usa userData.id quando válido; senão o último userId do identify na sessão.
 */
export function buildSegmentUserEventProperties(
  userData: SegmentUserEventData = {},
): Record<string, unknown> {
  const userId =
    toSegmentUserId(userData.id) ?? getLastIdentifiedSegmentUserId();

  const jusbillRaw = userData.jusbill_customer_id;
  const jusbill =
    jusbillRaw != null &&
      String(jusbillRaw).trim() !== '' &&
      String(jusbillRaw).toLowerCase() !== 'anonymous'
      ? jusbillRaw
      : null;

  const clientId = userData.client_id;
  const hasClientId =
    clientId != null && String(clientId).trim() !== '';

  return {
    ...(userId && { user_id: userId }),
    ...(userData.email && { user_email: userData.email }),
    ...(userData.name && { user_name: userData.name }),
    ...(hasClientId && { client_id: clientId }),
    ...(jusbill && { jusbill_customer_id: jusbill }),
  };
}

export function resetSegmentIdentity(): void {
  lastIdentifiedUserId = null;
  lastIdentifiedUserEmail = null;
  lastIdentifiedTraitsHash = null;
  getAnalytics()?.reset();
}
